import { createFileRoute, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useId, useState, type JSX } from 'react';

import { CtLogo } from '@/components/brand/index.js';
import { FullscreenFrame } from '@/features/diagrams/FullscreenFrame.js';
import { MermaidPreview } from '@/features/diagrams/MermaidPreview.js';
import {
  drawThumbnail,
  type ThumbnailDrawing,
} from '@/features/diagrams/thumbnail-render.js';
import {
  fetchSharedDiagram,
  type SharedDiagram,
} from '@/lib/api/diagram-shares.js';
import type { CanvasContent } from '@/lib/api/diagrams.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';
import { signInWithSso } from '@/lib/sso.js';
import { logger } from '@/logger.js';
import { getState as getIdentityState } from '@/store/identity-store.js';
import { PageContainer } from '@/ui/PageContainer.js';

/**
 * No `beforeLoad` and no `ensureDiagramsEnabled`, the same choice the shared dashboard route makes:
 * `boot.tsx` picks the gate-free public tree once from the first pathname, so a redirect to `/` from
 * here would land inside the app with no `AuthGate`. A switched-off Diagrams section is handled in
 * place instead, as an unavailable link (see `SharedDiagramPage`).
 */
export const Route = createFileRoute('/diagrams_/shared/$shareId')({
  component: SharedDiagramPage,
});

type ViewerState =
  | { kind: 'loading' }
  | { kind: 'ready'; diagram: SharedDiagram }
  | { kind: 'unavailable' }
  | { kind: 'error' };

/**
 * The chrome-free viewer for a diagram link.
 *
 * It renders with no session: the `/diagrams/shared/*` entry in `isPublicViewPath` keeps it out of
 * every gate and skips the identity and Teams bootstrap. It shows one pinned revision read-only and
 * never opens the live collaboration socket, so a link can neither edit nor follow the document.
 *
 * The service answers every refusal with the same 404, so there is no separate "sign in" answer as
 * dashboards have. A visitor with no session is offered sign-in on the unavailable screen instead,
 * because a link shared with them by name opens only once they are signed in.
 */
function SharedDiagramPage(): JSX.Element {
  const { shareId } = useParams({ from: '/diagrams_/shared/$shareId' });
  const [state, setState] = useState<ViewerState>({ kind: 'loading' });

  const load = useCallback(async (): Promise<void> => {
    if (!getRuntimeConfig().features.diagrams) {
      setState({ kind: 'unavailable' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const result = await fetchSharedDiagram(shareId);
      setState(result);
    } catch (reason: unknown) {
      logger.warn('Shared diagram fetch failed', reason);
      setState({ kind: 'error' });
    }
  }, [shareId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === 'loading') {
    return (
      <ViewerShell>
        <p className="text-sm text-muted-foreground" role="status">
          Loading diagram…
        </p>
      </ViewerShell>
    );
  }
  if (state.kind === 'unavailable') {
    return <UnavailableScreen onSignedIn={() => void load()} />;
  }
  if (state.kind === 'error') {
    return (
      <ViewerShell>
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm text-destructive">
            Something went wrong loading this diagram.
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="text-sm underline"
          >
            Try again
          </button>
        </div>
      </ViewerShell>
    );
  }
  return <ReadOnlyDiagram diagram={state.diagram} />;
}

/** The ready state: the title, which revision this is, and the drawing. */
function ReadOnlyDiagram({ diagram }: { diagram: SharedDiagram }): JSX.Element {
  return (
    <div
      className="flex h-full min-h-0 w-full flex-col"
      style={{ height: 'var(--app-height, 100dvh)' }}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border/40 bg-background px-4 py-3">
        <CtLogo
          variant="mark"
          tone="mono"
          className="size-6 text-[#18399b] [[data-theme='dark']_&]:text-white"
          aria-label="Customertimes"
        />
        <span className="text-sm font-medium text-foreground">
          Read-only diagram
        </span>
      </header>
      <div className="min-h-0 flex-1 overflow-auto">
        <PageContainer maxWidth="6xl">
          <article className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-elev-1">
            <header className="flex min-w-0 flex-col gap-0.5">
              <h1 className="truncate text-sm font-semibold text-foreground">
                {diagram.title}
              </h1>
              <p className="text-caption text-muted-foreground">
                {`Snapshot of revision ${diagram.revision}`}
              </p>
            </header>
            <DiagramDrawing diagram={diagram} />
          </article>
        </PageContainer>
      </div>
    </div>
  );
}

/**
 * Mermaid through the shared preview, which the recipient can zoom and pan like an editor can; a
 * whiteboard as a static picture; anything else explained.
 */
function DiagramDrawing({ diagram }: { diagram: SharedDiagram }): JSX.Element {
  const content = diagram.content;
  if (content.kind === 'mermaid') {
    return (
      <MermaidPreview
        source={content.source}
        readOnly
        className="h-[70vh] overflow-hidden rounded-lg border border-border"
      />
    );
  }
  if (content.kind === 'canvas') return <CanvasSnapshot content={content} />;
  return (
    <p className="text-sm text-muted-foreground">
      This version of the app cannot show this kind of diagram.
    </p>
  );
}

type CanvasState =
  { status: 'loading' } | { status: 'failed' } | ThumbnailDrawing;

/**
 * A whiteboard drawn with Excalidraw's exporter, the same one the gallery uses. The editor is never
 * mounted, so there is nothing to click that could change the drawing, and the exporter is loaded
 * only when a whiteboard link is actually opened.
 */
function CanvasSnapshot({ content }: { content: CanvasContent }): JSX.Element {
  const renderId = `shared-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [drawing, setDrawing] = useState<CanvasState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    drawThumbnail(content, renderId).then(
      (result) => {
        if (!cancelled) setDrawing(result);
      },
      (reason: unknown) => {
        logger.warn('Shared whiteboard render failed', reason);
        if (!cancelled) setDrawing({ status: 'failed' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [content, renderId]);

  if (drawing.status === 'loading') {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Drawing the whiteboard…
      </p>
    );
  }
  if (drawing.status === 'failed') {
    return (
      <p className="text-sm text-muted-foreground">
        This whiteboard cannot be drawn.
      </p>
    );
  }
  if (drawing.status === 'empty') {
    return (
      <p className="text-sm text-muted-foreground">This whiteboard is empty.</p>
    );
  }
  return (
    <FullscreenFrame className="h-[70vh] w-full">
      {(fullscreenControl) => (
        <>
          <div
            className="h-full w-full"
            dangerouslySetInnerHTML={{ __html: drawing.svg }}
          />
          <div className="absolute right-3 top-3">{fullscreenControl}</div>
        </>
      )}
    </FullscreenFrame>
  );
}

/**
 * Missing, revoked, expired or named for somebody else: the service does not say which, and neither
 * does this screen. Sign-in is offered only when there is no session, since a signed-in visitor who
 * lands here would get the same answer again.
 */
function UnavailableScreen({
  onSignedIn,
}: {
  onSignedIn: () => void;
}): JSX.Element {
  const signedIn = Boolean(getIdentityState().platformJwt);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSignIn(): Promise<void> {
    if (signingIn) return;
    setSigningIn(true);
    setError(null);
    try {
      await signInWithSso();
      onSignedIn();
    } catch {
      setError('Sign-in failed. Try again.');
      setSigningIn(false);
    }
  }

  return (
    <ViewerShell>
      <div className="flex flex-col items-center gap-4">
        <p className="max-w-sm text-sm text-muted-foreground">
          This diagram is unavailable, has been revoked or has expired. If it
          was shared with you personally, sign in and open the link again.
        </p>
        {signedIn ? null : (
          <button
            type="button"
            onClick={() => void onSignIn()}
            disabled={signingIn}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground transition-all hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {signingIn ? 'Signing in…' : 'Sign in'}
          </button>
        )}
        {error !== null && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </ViewerShell>
  );
}

/** The full-screen frame for the loading, unavailable and error states. */
function ViewerShell({ children }: { children: JSX.Element }): JSX.Element {
  return (
    <div
      className="flex w-full flex-col items-center justify-center gap-6 bg-background px-6 text-center"
      style={{ height: 'var(--app-height, 100dvh)' }}
    >
      <div className="flex items-center gap-3">
        <CtLogo
          variant="mark"
          tone="mono"
          className="size-9 text-[#18399b] [[data-theme='dark']_&]:text-white"
          aria-label="Customertimes"
        />
        <span className="font-heading text-xl font-bold tracking-tight text-foreground">
          Agent
        </span>
      </div>
      {children}
    </div>
  );
}
